<template>
  <div class="playtimes-tables">
    <div v-if="hasMultipleWeeks" class="playtimes-tables__pagination">
      <div class="playtimes-tables__pagination__prev">
        <button @click="handlePrevWeek" :disabled="isFirstWeek">
          <ui-icon icon="chevron-20" rotation="180"></ui-icon>
          <span>Vorherige Woche</span>
        </button>
      </div>
      <div class="playtimes-tables__pagination__next">
        <button @click="handleNextWeek" :disabled="isLastWeek">
          <span>Nächste Woche</span>
          <ui-icon icon="chevron-20"></ui-icon>
        </button>
      </div>
    </div>

    <div class="playtimes-tables__carrier" data-js-perf-sticky-table ref="stickyTableRef">
      <div ref="stickySentinelRef" data-sticky-sentinel></div>
      <div class="table-responsive" data-js-perf-sticky-table-horizontal ref="horizontalTableRef">
        <div class="playtimes-table-sizer">
          <template v-if="currentWeek.length > 0">
            <div class="playtimes-table-head schedules-bg-layer" aria-hidden="true">
              <div class="playtimes-sticky-title" v-html="movie.filmTitle"></div>
              <div class="playtimes-table-head__weekdays">
                <div
                  v-for="(weekday, index) in currentWeekHeaders"
                  :key="`${index}-head`"
                  :class="[
                    'weekday-head',
                    { 'weekday-head--highlighted': isToday(weekday) },
                    { 'weekday-head--empty': weekday.performanceCount === 0 },
                  ]"
                >
                  {{ isToday(weekday) ? "Heute" : weekday.headline }}
                </div>
              </div>
            </div>
            <div
              class="playtimes-table-wrapper schedules-bg-layer"
              v-for="(performanceGroup, index) in currentWeek"
              :key="index"
            >
              <!-- Performance Attributes -->
              <div
                v-if="performanceGroup && performanceGroup.hasAttributes"
                class="performance-attributes-wrapper"
                data-js-perf-sticky-table-width
              >
                <div class="performance-attributes" data-js-performance-attributes>
                  <ul>
                    <li v-if="performanceGroup.isCombinedSite" class="sitename">
                      <span>{{ performanceGroup.siteName }}</span>
                    </li>
                    <li
                      v-for="attribute in performanceGroup.attributeNameSlug"
                      :key="attribute.name"
                    >
                      <span>
                        <a v-if="attribute.slug" :href="`/mehr/filmreihe/${attribute.slug}`">
                          <span v-html="attribute.name"></span>
                        </a>
                        <span v-else v-html="attribute.name"></span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div
                v-if="
                  !loading &&
                  getGroupFirstPerformanceWeekIndex(performanceGroup) &&
                  getGroupFirstPerformanceWeekIndex(performanceGroup) > currentWeekNumber &&
                  getGroupFirstPerformance(performanceGroup)?.unixdatetime
                "
                class="playtimes-table__jump"
              >
                <button
                  class="btn btn-outline"
                  @click="handleJumpToWeek(getGroupFirstPerformanceWeekIndex(performanceGroup))"
                >
                  Zum ersten Termin springen:
                  {{ unixToDateString(getGroupFirstPerformance(performanceGroup).unixdatetime) }}
                </button>
              </div>
              <div v-else class="playtimes-table">
                <table>
                  <thead class="visually-hidden">
                    <tr>
                      <th
                        v-for="(weekday, index) in performanceGroup.weekData"
                        :key="`weekday-${index}`"
                        scope="col"
                        :class="{
                          'weekday-head--highlighted': isToday(weekday),
                        }"
                      >
                        {{ isToday(weekday) ? "Heute" : weekday.headline }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="performanceIndex in getMaxTimeSlots(performanceGroup.weekData)"
                      :key="`timeslot-${performanceIndex}`"
                    >
                      <td
                        v-for="(weekday, dayIndex) in performanceGroup.weekData"
                        :key="`day-${dayIndex}-slot-${performanceIndex}`"
                        class="timetable-td"
                      >
                        <PlaytimeButton
                          v-if="
                            weekday.times &&
                            weekday.times[performanceIndex] &&
                            weekday.times[performanceIndex].unixdatetime &&
                            !weekday.times[performanceIndex].isPlaceholder
                          "
                          :key="weekday.times[performanceIndex].performanceID"
                          :performance="weekday.times[performanceIndex]"
                          :show-date="false"
                          :movie="movie"
                        />
                        <template v-else>
                          <div
                            :class="[
                              'playtime-button',
                              'disabled',
                              { 'playtime-button--today': isToday(weekday) },
                            ]"
                            disabled
                          ></div>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useDateHelpers, unixToDateString } from "../composables/useDateHelpers.js";
import PlaytimeButton from "./PlaytimeButton.vue";

const { isToday, formatDateTitle } = useDateHelpers();

const props = defineProps({
  performanceGroups: {
    type: Array,
    required: true,
  },
  movie: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const allPerformances = computed(() => {
  const groups = Array.isArray(props.performanceGroups) ? props.performanceGroups : [];
  const performances = groups.flatMap((group) =>
    group?.performances ? Object.values(group.performances) : [],
  );
  return performances.sort((a, b) => (a.unixdatetime ?? 0) - (b.unixdatetime ?? 0));
});

function weeksFromSameWeekdaySpans(startDate, endDate) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    throw new Error("Both arguments must be Date objects");
  }

  // Ensure start <= end
  let start = startDate;
  let end = endDate;
  if (end < start) [start, end] = [end, start];

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Convert to UTC midnights of the *local* calendar dates
  const startUTC0 = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUTC0 = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  // Exact whole-day distance (no DST drift)
  const diffDays = Math.round((endUTC0 - startUTC0) / DAY_MS);

  // Count how many 7-day bins (starting on start's weekday) are touched, inclusive
  return Math.floor(diffDays / 7) + 1;
}

const today = new Date();
const currentWeekNumber = ref(1);

// Template refs for scrolling and sticky behavior
const stickyTableRef = ref(null);
const stickySentinelRef = ref(null);
const horizontalTableRef = ref(null);

// Sticky header state
let stickyTicking = false;
let stickyAnimationId = null;

/**
 * Update the --sticky-offset CSS variable based on scroll position
 * Uses the native sticky sentinel to detect when header should stick
 */
const updateStickyOffset = () => {
  if (!stickyTableRef.value || !stickySentinelRef.value) {
    stickyTicking = false;
    return;
  }

  const containerEl = stickyTableRef.value;
  const sentinel = stickySentinelRef.value;

  // Get the header height from CSS variable or default to 0
  const headerHeightStr =
    getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "0px";
  const headerHeight = parseFloat(headerHeightStr);

  // Get current positions
  const sentinelRect = sentinel.getBoundingClientRect();
  const containerRect = containerEl.getBoundingClientRect();

  // Calculate the offset: when sentinel is stuck, it's at headerHeight from viewport top
  // The offset is how far the sentinel has moved from the container's top
  // When not stuck: sentinelRect.top === containerRect.top (relative to container), offset = 0
  // When stuck: sentinelRect.top === headerHeight, calculate the difference
  const sentinelTop = sentinelRect.top;
  const containerTop = containerRect.top;
  const containerHeight = containerRect.height;
  const maxOffset = Math.max(0, containerHeight - 180);
  const stickyOffset = Math.max(0, Math.min(maxOffset, sentinelTop - containerTop));

  // Set CSS variable on the parent container
  containerEl.style.setProperty("--sticky-offset", `${stickyOffset.toFixed(2)}px`);

  stickyTicking = false;
};

const onStickyScroll = () => {
  if (!stickyTicking) {
    stickyTicking = true;
    stickyAnimationId = requestAnimationFrame(updateStickyOffset);
  }
};

const lastPerformanceDate = computed(() => {
  if (allPerformances.value.length === 0) return new Date();
  return new Date(allPerformances.value[allPerformances.value.length - 1].unixdatetime * 1000);
});

const weekTotalCount = computed(() => {
  return weeksFromSameWeekdaySpans(today, lastPerformanceDate.value);
});

const handleDateFilterChanged = async () => {
  currentWeekNumber.value = 1; // reset to first week
  await scrollToFirstPerformance();
};

const updateScrollableClass = () => {
  if (!horizontalTableRef.value) return;
  horizontalTableRef.value.classList.toggle(
    "is-scrollable",
    horizontalTableRef.value.scrollWidth > horizontalTableRef.value.clientWidth,
  );
};

let resizeObserver = null;

onMounted(() => {
  window.addEventListener("date-filter-changed", handleDateFilterChanged);
  window.addEventListener("scroll", onStickyScroll);

  resizeObserver = new ResizeObserver(updateScrollableClass);
  if (horizontalTableRef.value) resizeObserver.observe(horizontalTableRef.value);

  updateStickyOffset();
  updateScrollableClass();
});

onUnmounted(() => {
  window.removeEventListener("date-filter-changed", handleDateFilterChanged);
  window.removeEventListener("scroll", onStickyScroll);
  resizeObserver?.disconnect();

  if (stickyAnimationId) cancelAnimationFrame(stickyAnimationId);
});

const groupPerformancesByDate = (performances) => {
  const grouped = {};

  Object.values(performances).forEach((performance) => {
    const date = performance.date || "";
    if (!grouped[date]) {
      grouped[date] = {};
    }
    grouped[date][performance.performanceID] = performance;
  });

  return grouped;
};

const generateWeekData = (performancesByDate) => {
  const tablePerformances = {};

  let currentWeekStart = new Date(today);
  currentWeekStart.setHours(0, 0, 0, 0);

  // Generate weekTotalCount.value weeks of data
  for (let weekCount = 1; weekCount <= weekTotalCount.value; weekCount++) {
    for (let day = 1; day <= 7; day++) {
      const currentDate = new Date(currentWeekStart);
      currentDate.setDate(currentWeekStart.getDate() + day);
      const dateString = currentDate.toISOString().split("T")[0];

      const datePerformances = performancesByDate[dateString] || {};

      // Sort performances by unixdatetime
      const sortedPerformances = Object.values(datePerformances).sort(
        (a, b) => (a.unixdatetime ?? 0) - (b.unixdatetime ?? 0),
      );

      tablePerformances[dateString] = {
        headline: formatDateTitle(dateString, false),
        times: sortedPerformances,
        performanceCount: sortedPerformances.length,
        performanceWeek: weekCount,
        date: dateString,
      };
    }

    // Move to next week
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  return tablePerformances;
};

const performanceGroupsByWeek = computed(() => {
  if (!props.performanceGroups?.length) {
    return {};
  }

  const perfGroupsByWeek = {};

  props.performanceGroups.forEach((perfGroup) => {
    const performances = perfGroup.performances;

    if (!performances || Object.keys(performances).length === 0) return;

    // Group performances by date for efficient lookup
    const performancesByDate = groupPerformancesByDate(performances);

    // Generate weekTotalCount weeks of data starting from today
    const tablePerformances = generateWeekData(performancesByDate);

    // Group table performances by week
    const weeks = {};
    Object.values(tablePerformances).forEach((tablePerformance) => {
      const weekNumber = tablePerformance.performanceWeek;
      if (!weeks[weekNumber]) {
        weeks[weekNumber] = [];
      }
      weeks[weekNumber].push({ ...tablePerformance });
    });

    // Set max performance count and add performance group to each week
    Object.entries(weeks).forEach(([weekNumber, weekData]) => {
      const maxPerformanceCount = Math.max(...weekData.map((d) => d.performanceCount || 0));

      weekData.forEach((dayData) => {
        dayData.maxPerformanceCount = maxPerformanceCount;
      });

      if (!perfGroupsByWeek[weekNumber]) {
        perfGroupsByWeek[weekNumber] = [];
      }

      perfGroupsByWeek[weekNumber].push({
        ...perfGroup,
        weekData,
        weekNumber: parseInt(weekNumber),
        maxPerformanceCount,
      });
    });
  });

  // Sort weeks by week number and sort groups within each week
  const sortedWeeks = {};
  Object.keys(perfGroupsByWeek)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .forEach((weekNumber) => {
      // Sort groups: groups with attributes first, groups without attributes last
      const sortedGroups = perfGroupsByWeek[weekNumber].sort((a, b) => {
        const aHasAttributes = a.hasAttributes ? 1 : 0;
        const bHasAttributes = b.hasAttributes ? 1 : 0;
        // Groups with attributes (1) come before groups without (0)
        return bHasAttributes - aHasAttributes;
      });
      sortedWeeks[weekNumber] = sortedGroups;
    });

  return sortedWeeks;
});

const currentWeek = computed(() => performanceGroupsByWeek.value[currentWeekNumber.value] || []);

// Get the weekday headers from the first performance group's weekData
const currentWeekHeaders = computed(() => {
  if (currentWeek.value.length === 0) return [];
  return currentWeek.value[0].weekData || [];
});

const weeksCount = computed(() => Object.keys(performanceGroupsByWeek.value).length);

const getGroupFirstPerformance = (performanceGroup) => {
  if (!performanceGroup?.performances) return null;
  const performances = Object.values(performanceGroup.performances);
  if (performances.length === 0) return null;
  return performances.sort((a, b) => (a.unixdatetime ?? 0) - (b.unixdatetime ?? 0))[0];
};

const getGroupFirstPerformanceWeekIndex = (performanceGroup) => {
  const weekNumbers = Object.keys(performanceGroupsByWeek.value)
    .map(Number)
    .sort((a, b) => a - b);

  for (const weekNumber of weekNumbers) {
    const groups = performanceGroupsByWeek.value[weekNumber];
    const match = groups.find((g) => g.performances === performanceGroup.performances);
    if (match?.weekData.some((day) => day.performanceCount > 0)) {
      return weekNumber;
    }
  }

  return null;
};

/**
 * Get performance indices for table rows
 * Returns an array of indices (e.g., [0, 1, 2, 3]) based on max performance count
 */
const getMaxTimeSlots = (weekData) => {
  if (!weekData || weekData.length === 0) return [];

  // Get max performance count from the first day (all days have the same maxPerformanceCount)
  const maxCount = Math.max(1, weekData[0]?.maxPerformanceCount || 0);

  // Return array of indices [0, 1, 2, ..., maxCount-1]
  return Array.from({ length: maxCount }, (_, i) => i);
};

const isOutOfBounds = (weekNumber) => {
  if (!weekNumber) return true;
  if (weekNumber < 1 || weekNumber > weeksCount.value) return true;
  return false;
};

const isLastWeek = computed(() => currentWeekNumber.value === weeksCount.value);

const isFirstWeek = computed(() => currentWeekNumber.value === 1);

const hasMultipleWeeks = computed(() => weeksCount.value > 1);

/**
 * Scroll to the first performance button in the current week
 * Centers the first button horizontally in the scrollable container
 */
const scrollToFirstPerformance = async () => {
  await nextTick();

  if (!horizontalTableRef.value || !stickyTableRef.value) return;

  // Find the first playtime button that is not disabled
  const button = stickyTableRef.value.querySelector(
    ".playtime-button:not(.disabled):not(.playtime-button--today)",
  );

  if (!button || !horizontalTableRef.value) return;

  const btnRect = button.getBoundingClientRect();
  const containerRect = horizontalTableRef.value.getBoundingClientRect();
  const btnOffsetLeft = btnRect.left - containerRect.left;
  const scrollPosition = btnOffsetLeft - containerRect.width / 2 + btnRect.width / 2;

  horizontalTableRef.value.scroll({
    left: scrollPosition,
    behavior: "smooth",
  });
};

const handleJumpToWeek = async (weekNumber) => {
  if (!weekNumber || isOutOfBounds(weekNumber)) return;
  currentWeekNumber.value = weekNumber;
  await scrollToFirstPerformance();
};

const handlePrevWeek = async () => {
  await handleJumpToWeek(currentWeekNumber.value - 1);
};

const handleNextWeek = async () => {
  await handleJumpToWeek(currentWeekNumber.value + 1);
};
</script>

<style>
[data-sticky-sentinel] {
  position: sticky;
  top: var(--header-height, 0px);
  height: 0;
  width: 100%;
  pointer-events: none;
  z-index: -1;
}
</style>
